import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useMemo } from 'react';
import LineChart from '../LineChart';
import { apiGet } from '../../services/_common';
import SkeletonTable from '../SkeletonTable';
import IssueGuide from '../IssueGuide';
import { IUser } from '../../atoms';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';

const Root = styled.div`
  height: 100%;

  .widget-header {
    padding: 1.5rem 1.5rem 0 1.5rem;
    > .widget-title {
      > span {
        display: flex;
        align-items: flex-start;
        flex-direction: column;
        gap: 1rem;
        font-size: var(--widget-font-size-xl);
        color: var(--widget-text-primary);
        font-weight: var(--font-medium);
      }
    }
  }

  .widget-body {
    padding: 0 1.5rem 1.5rem 1.5rem;
    width: 100%;
    height: 100%;
  }

  .infoWrapper {
    display: flex;
    gap: 1rem;
    justify-content: flex-start;
    width: 100%;
    height: 10rem;
    padding-top: 1rem;

    .infoItem {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      gap: 0.5rem;
      flex: 0.5;
      min-height: 0; // 최소 높이 제한 해제

      &.theme-lightblue {
        color: var(--widget-secondary);
      }
      &.theme-blue {
        color: var(--widget-primary);
      }
      &.theme-lightblue {
        .infoItem-color {
          background-color: var(--widget-secondary);
        }
      }
      &.theme-blue {
        .infoItem-color {
          background-color: var(--widget-primary);
        }
      }
      .infoItem-label {
        font-size: var(--widget-font-size-xl);
        color: var(--widget-text-tertiary);
        flex-shrink: 0; // 라벨이 축소되지 않도록
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .infoItem-color {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: var(--widget-font-size-2xl);
          width: 3rem;
          height: 3rem;
          border-radius: 0.5rem;
          color: var(--white);
          > span {
            font-size: var(--widget-font-size-lg);
            font-variation-settings: 'FILL' 1, 'wght' 600, 'GRAD' 200, 'opsz' 40;
            color: #fff;
          }
        }
      }

      .infoItem-value {
        font-size: var(--widget-font-size-6xl);
        font-weight: var(--font-bold);
        text-align: end;
        width: 100%;
        line-height: 1; // 줄 간격 최소화
        flex-shrink: 1; // 값이 축소될 수 있도록

        .infoItem-unit {
          color: var(--widget-text-tertiary);
          font-size: var(--widget-font-size-xl);
          margin-left: 0.5rem;
        }
      }
    }
  }

  .lineChartWrapper {
    width: 100%;
    height: calc(100% - 10rem); /* infoWrapper 높이만큼 빼기 */
  }

  .skeletonWrapper {
    width: 100%;
    height: 100%;
  }
`;

// 주간 출역 그래프 (꺾은선)
export const WeekAttendLineGraph = ({ uniqueKey, userInfo, refetchInterval }: { uniqueKey: string; userInfo: IUser; refetchInterval?: number }) => {
  const isSiteDashboard = refetchInterval !== undefined; // 상황판여부
  const { hCd, sCd } = userInfo;
  const { t } = useTranslation();
  const [attendLineData, setAttendLineData] = useState<any[]>([]);
  const [formattedDates, setFormattedDates] = useState<string[]>([]);

  const {
    data: newWorkerData,
    isLoading: isLoadingNewWorkerData,
    isError: isErrorNewWorkerData,
  } = useQuery(['TotalInfoNewWorkerGet', uniqueKey, userInfo], () => fetchNewWorkerData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }),
  });

  const {
    data: attendData,
    isLoading: isLoadingAttendData,
    isError: isErrorAttendData,
  } = useQuery(['attendCntGet', uniqueKey, userInfo], () => fetchAttendData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }),
  });

  // 공통 날짜 범위 계산 함수
  const getDateRange = () => {
    const today = dayjs();
    const sevenDaysAgo = today.subtract(7, 'day');
    return {
      startDate: sevenDaysAgo.format('YYYYMMDD'),
      endDate: today.format('YYYYMMDD'),
    };
  };

  // 공통 날짜별 그룹핑 함수
  const groupByDate = (dataList: any[], dateField: string) => {
    // 7일간의 모든 날짜 생성
    const today = dayjs();
    const allDates = Array.from({ length: 7 }, (_, i) => today.subtract(6 - i, 'day').format('YYYY-MM-DD'));

    // 데이터가 있는 날짜들을 그룹핑
    const grouped = dataList.reduce((acc: { [key: string]: number }, item: any) => {
      const date = item[dateField];
      if (date) {
        acc[date] = (acc[date] || 0) + 1;
      }
      return acc;
    }, {});

    // 모든 날짜에 대해 데이터가 있으면 해당 값, 없으면 0으로 설정
    return allDates.map((date) => ({
      date,
      count: grouped[date] || 0,
    }));
  };

  // 오늘로부터 7일간 신규 근로자 인원 수 조회
  const fetchNewWorkerData = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      const req = { wWorkstatus: 'I', wJobdate1: startDate, wJobdate2: endDate, hCd, sCd };
      const res = await apiGet({ path: '/worker', req });

      if (res.status === 200) {
        const { workerList } = res.data.data;
        const dailyNewWorkerCount = groupByDate(workerList, 'wJobdate');

        return {
          newWorkerCnt: workerList.length,
          dailyNewWorkerCount,
        };
      }
      return undefined;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // 오늘로부터 7일간 출역 조회
  const fetchAttendData = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      const req = { aDate1: startDate, aDate2: endDate, hCd, sCd };
      const res = await apiGet({ path: '/attend/day', req });

      if (res.status === 200) {
        const { attendList } = res.data.data;
        const dailyAttendCount = groupByDate(attendList, 'aDate');

        return {
          todayAttendCnt: attendList.length,
          dailyAttendCount,
        };
      }
      return undefined;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // LineChart용 데이터 생성
  useEffect(() => {
    const newWorkerCounts = newWorkerData?.dailyNewWorkerCount?.map((item: any) => item.count) || [];
    const attendCounts = attendData?.dailyAttendCount?.map((item: any) => item.count) || [];
    const sortedDates = newWorkerData?.dailyNewWorkerCount?.map((item: any) => item.date) || [];

    // 날짜를 MM/DD(weekday) 형식으로 변환
    const formattedDatesArray = sortedDates.map((date) => {
      const dayjsDate = dayjs(date);
      const weekday = dayjsDate.format('ddd'); // 월, 화, 수, 목, 금, 토, 일
      return `${dayjsDate.format('MM/DD')}(${weekday})`;
    });

    // CSS 변수에서 색상 값 가져오기
    const lightblueColor = getComputedStyle(document.documentElement).getPropertyValue('--widget-secondary').trim();
    const blueColor = getComputedStyle(document.documentElement).getPropertyValue('--widget-primary').trim();

    // LineChart 데이터 형식으로 변환
    const lineData = [
      {
        label: t('신규 근로자'),
        data: newWorkerCounts,
        borderColor: lightblueColor,
        backgroundColor: 'transparent', // 배경색 제거
        fill: false, // 영역 채우기 비활성화
        tension: 0, // 직선으로 표현 (0 = 직선, 1 = 곡선)
        pointRadius: 4, // 점 크기
        pointHoverRadius: 6, // 호버 시 점 크기
        pointBackgroundColor: lightblueColor, // 점 배경색
        pointBorderColor: lightblueColor, // 점 테두리색
        pointBorderWidth: 2, // 점 테두리 두께
      },
      {
        label: t('전체 출역'),
        data: attendCounts,
        borderColor: blueColor,
        backgroundColor: 'transparent', // 배경색 제거
        fill: false, // 영역 채우기 비활성화
        tension: 0, // 직선으로 표현 (0 = 직선, 1 = 곡선)
        pointRadius: 4, // 점 크기
        pointHoverRadius: 6, // 호버 시 점 크기
        pointBackgroundColor: blueColor, // 점 배경색
        pointBorderColor: blueColor, // 점 테두리색
        pointBorderWidth: 2, // 점 테두리 두께
      },
    ];

    setAttendLineData(lineData);
    setFormattedDates(formattedDatesArray);
  }, [newWorkerData, attendData, t]);

  if (isErrorNewWorkerData || isErrorAttendData) return <IssueGuide />;
  const loading = !newWorkerData || !attendData || isLoadingNewWorkerData || isLoadingAttendData;

  return (
    <Root>
      <div className='widget-header'>
        <div className='widget-title flex-between'>
          <span>{t('주간 출역 그래프')}</span>
        </div>
      </div>
      {loading ? (
        <div className='skeletonWrapper'>
          <SkeletonTable rows={0} mainRowHeight={420} mainRowWidth='100%' />
        </div>
      ) : (
        <div className='widget-body'>
          <div className='lineChartWrapper'>
            <LineChart labels={formattedDates} datasets={attendLineData} title='' showLegend={false} tension={0} pointRadius={4} pointHoverRadius={6} />
          </div>
          <div className='infoWrapper'>
            <div className='infoItem theme-lightblue'>
              <div className='infoItem-label'>
                <div className='infoItem-color'>
                  <span className='material-symbols-rounded'>person_add</span>
                </div>
                {t('신규 근로자')}
              </div>
              <div className='infoItem-value'>
                {newWorkerData?.newWorkerCnt?.toLocaleString() ?? 0}
                <span className='infoItem-unit'>{t('명')}</span>
              </div>
            </div>
            <div className='infoItem theme-blue'>
              <div className='infoItem-label'>
                <div className='infoItem-color'>
                  <span className='material-symbols-rounded'>leaderboard</span>
                </div>
                {t('전체 출역')}
              </div>
              <div className='infoItem-value'>
                {attendData?.todayAttendCnt?.toLocaleString() ?? 0}
                <span className='infoItem-unit'>{t('명')}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Root>
  );
};
